import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { WorkloadBadge } from "@/components/Badges";
import { Plus, X, GitFork, Users } from "lucide-react";
import { toast } from "sonner";

const toIso = (v) => (v ? new Date(v + ":00+07:00").toISOString() : null);

export default function DelegateDialog({ open, onOpenChange, task, onDone }) {
  const [mode, setMode] = useState("utuh");
  const [positions, setPositions] = useState([]);
  const [workload, setWorkload] = useState([]);
  const [utuhPos, setUtuhPos] = useState("");
  const [utuhDeadline, setUtuhDeadline] = useState("");
  const [rows, setRows] = useState([{ title: "", assignee_position_id: "", weight: 1, deadline: "", estimated_hours: 4 }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/org/positions").then((r) => setPositions(r.data.filter((p) => !p.is_vacant || p.has_plt)));
    api.get("/tasks/workload/subordinates").then((r) => setWorkload(r.data)).catch(() => {});
  }, [open]);

  const wlOf = (uid) => workload.find((w) => w.user?.id === uid);

  const submit = async () => {
    setLoading(true);
    try {
      let payload;
      if (mode === "utuh") {
        if (!utuhPos) { setLoading(false); return toast.error("Pilih penerima delegasi"); }
        payload = { mode: "utuh", assignee_position_id: utuhPos, deadline: toIso(utuhDeadline) || task.deadline_current };
      } else {
        const valid = rows.filter((r) => r.assignee_position_id && r.title);
        if (valid.length === 0) { setLoading(false); return toast.error("Isi minimal 1 baris delegasi"); }
        payload = { mode: "pecah", assignments: valid.map((r) => ({ ...r, deadline: toIso(r.deadline) || task.deadline_current, weight: Number(r.weight), estimated_hours: Number(r.estimated_hours) })) };
      }
      await api.post(`/tasks/${task.id}/delegate`, payload);
      toast.success("Tugas berhasil didelegasikan");
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mendelegasikan"); }
    setLoading(false);
  };

  const PositionSelect = ({ value, onChange, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Pilih posisi bawahan" /></SelectTrigger>
      <SelectContent>
        {positions.map((p) => {
          const wl = wlOf(p.holder?.id);
          return (
            <SelectItem key={p.id} value={p.id}>
              {p.title} — {p.holder?.name || "(kosong)"}{wl ? ` · ${wl.utilization}%` : ""}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto zoa-scroll">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitFork className="h-5 w-5 text-indigo-500" />Delegasikan Tugas</DialogTitle>
        </DialogHeader>

        <RadioGroup value={mode} onValueChange={setMode} className="flex gap-4 mb-2">
          <label className="flex items-center gap-2 text-sm font-medium"><RadioGroupItem value="utuh" data-testid="delegate-mode-utuh" />Teruskan Utuh (Anda jadi checker)</label>
          <label className="flex items-center gap-2 text-sm font-medium"><RadioGroupItem value="pecah" data-testid="delegate-mode-pecah" />Pecah per Langkah</label>
        </RadioGroup>

        {workload.length > 0 && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><Users className="h-3.5 w-3.5" />Beban Kerja Calon Penerima</p>
            <div className="flex flex-wrap gap-2">
              {workload.map((w) => (
                <span key={w.user?.id} className="inline-flex items-center gap-1.5 text-xs">
                  <span className="text-slate-600">{w.user?.name}</span>
                  <WorkloadBadge level={w.level} utilization={w.utilization} />
                </span>
              ))}
            </div>
          </div>
        )}

        {mode === "utuh" ? (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Penerima</Label><PositionSelect value={utuhPos} onChange={setUtuhPos} testid="delegate-utuh-position" /></div>
            <div><Label>Deadline</Label><Input type="datetime-local" value={utuhDeadline} onChange={(e) => setUtuhDeadline(e.target.value)} /></div>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((r, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2 relative">
                {rows.length > 1 && <button onClick={() => setRows((a) => a.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-slate-400 hover:text-rose-500"><X className="h-4 w-4" /></button>}
                <Input placeholder="Judul sub-tugas" value={r.title} onChange={(e) => setRows((a) => a.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
                <PositionSelect value={r.assignee_position_id} onChange={(v) => setRows((a) => a.map((x, j) => (j === i ? { ...x, assignee_position_id: v } : x)))} />
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-xs">Bobot</Label><Input type="number" value={r.weight} onChange={(e) => setRows((a) => a.map((x, j) => (j === i ? { ...x, weight: e.target.value } : x)))} /></div>
                  <div><Label className="text-xs">Est. Jam</Label><Input type="number" value={r.estimated_hours} onChange={(e) => setRows((a) => a.map((x, j) => (j === i ? { ...x, estimated_hours: e.target.value } : x)))} /></div>
                  <div><Label className="text-xs">Deadline</Label><Input type="datetime-local" value={r.deadline} onChange={(e) => setRows((a) => a.map((x, j) => (j === i ? { ...x, deadline: e.target.value } : x)))} /></div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setRows((a) => [...a, { title: "", assignee_position_id: "", weight: 1, deadline: "", estimated_hours: 4 }])}><Plus className="h-4 w-4 mr-1" />Tambah penerima</Button>
          </div>
        )}
        <p className="text-xs text-slate-400">Deadline sub-tugas tidak boleh melewati deadline induk. Tugas ke divisi lain otomatis menunggu persetujuan Kepala Divisi tujuan.</p>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={submit} disabled={loading} data-testid="delegate-submit-button" className="bg-indigo-600 hover:bg-indigo-700">{loading ? "Memproses..." : "Delegasikan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
